import { Assistant } from "@inrev/assistant";
import { type FileId, fileId } from "@inrev/common";
import { Option } from "@inrev/core";
import type { Nodes } from "mdast";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import { unified } from "unified";
import * as z from "zod";

/**
 * The citation grammar lives in `Assistant.Text`. The link text is a placeholder: the panel numbers
 * citations by first appearance, so the same record and page reuse one number.
 */
const PREVIEWABLE_FILE = /\.pdf$/i;
const TEXT_CITATION = new RegExp(Assistant.Text.TEXT_CITATION_PATTERN, "gi");
const EVIDENCE_TOKENS = new RegExp(Assistant.Text.EVIDENCE_TOKEN_PATTERN, "gi");

const evidenceKind = z.enum(Assistant.Tools.EvidenceId.kinds);
type EvidenceKind = z.infer<typeof evidenceKind>;

const KIND_LABELS: Record<EvidenceKind, string> = {
	account: "Account",
	application_extraction: "Application extraction",
	bond: "Bond",
	bond_request: "Bond request",
	bond_request_extraction: "Bond request extraction",
	bonding_line: "Bonding line",
	contract_extraction: "Contract extraction",
	credit_pull: "Credit pull",
	file: "Document",
	financial_extraction: "Financial extraction",
	follower: "Follower",
	indemnification: "Indemnification",
	indemnity_agreement: "Indemnity agreement",
	ofac_check: "OFAC check",
	party: "Party",
	period: "Financial period",
	statement: "Financial statement",
	tag: "Tag",
	underwriting: "Underwriting",
	wip_extraction: "WIP extraction",
	wip_line_item: "WIP line item",
	wip_project: "WIP project",
	wip_statement: "WIP statement",
};

export type Citation = { kind: EvidenceKind; id: string; page?: number; title?: string };

/** The evidence id a citation points at, the key its database label is looked up by. */
export const citationEvidenceId = (citation: Citation): Assistant.Tools.EvidenceId | undefined => {
	const parsed = Assistant.Tools.EvidenceId.schema.safeParse(`${citation.kind}:${citation.id}`);
	return parsed.success ? parsed.data : undefined;
};

/** A cited PDF, which the preview panel can open at its first cited page. */
export type DocumentCitation = { fileId: FileId; page?: number; title?: string };

export const isCitationHref = (href: string | undefined): href is string =>
	href?.startsWith(Assistant.Text.CITATION_SCHEME) ?? false;

/** The record a citation points at, or undefined when its href is malformed. */
export const parseCitation = (href: string, title: string | undefined): Citation | undefined => {
	const match = Assistant.Text.CITATION_HREF.exec(href);
	if (match === null) return undefined;
	const kind = evidenceKind.safeParse(match[1].toLowerCase());
	if (!kind.success) return undefined;
	const page = match[3] === undefined ? undefined : Number(match[3]);
	return {
		kind: kind.data,
		id: match[2].toLowerCase(),
		page: page !== undefined && page > 0 ? page : undefined,
		title: title === "" ? undefined : title,
	};
};

/**
 * What a citation's number is keyed by: the record and page it points at, so rewording its title
 * or dropping it (an older bare id) keeps the number. A malformed href keys on itself.
 */
export const citationKey = (href: string): string => {
	const citation = parseCitation(href, undefined);
	if (citation === undefined) return href;
	return `${citation.kind}:${citation.id}${citation.page === undefined ? "" : `#${citation.page}`}`;
};

export const citationKindLabel = (citation: Citation): string => KIND_LABELS[citation.kind];

/** Splits `"Name.pdf, p. 8"` into the file name and the location within it. */
export const citationParts = (
	title: string | undefined,
): { fileName: string; location?: string } => {
	if (title === undefined) return { fileName: "Document" };
	const split = title.lastIndexOf(", ");
	return split === -1
		? { fileName: title }
		: { fileName: title.slice(0, split), location: title.slice(split + 2) };
};

/** The citation as a document the preview can open; only PDFs, which the browser's viewer renders. */
export const documentCitation = (citation: Citation): DocumentCitation | undefined => {
	if (citation.kind !== "file") return undefined;
	if (!PREVIEWABLE_FILE.test(citationParts(citation.title).fileName)) return undefined;
	const parsed = fileId.safeParse(citation.id);
	return parsed.success
		? { fileId: parsed.data, page: citation.page, title: citation.title }
		: undefined;
};

/** Parses an answer the way `AdminAssistantMarkdown` renders it, GFM included. */
export const assistantMarkdown = unified().use(remarkParse).use(remarkGfm);

type CitationLink = { href: string; title: string | undefined };

type FoundCitation = { start: number; end: number } & (
	| { kind: "link"; link: CitationLink }
	| { kind: "text"; hrefs: string[] }
);

const toCitationHref = (token: string): string =>
	`${Assistant.Text.CITATION_SCHEME}${token.replace(/^cite:/i, "")}`;

const textCitations = (source: string, offset: number): FoundCitation[] =>
	[...source.matchAll(TEXT_CITATION)].map(({ 0: whole, index }) => ({
		kind: "text",
		start: offset + index,
		end: offset + index + whole.length,
		hrefs: [...whole.matchAll(EVIDENCE_TOKENS)].map(([token]) => toCitationHref(token)),
	}));

/** Only plain text nodes are searched, so ids in code, link text, and link titles stay as written. */
const findCitations = (text: string): FoundCitation[] => {
	const found: FoundCitation[] = [];
	const visit = (node: Nodes): void => {
		const start = node.position?.start.offset;
		const end = node.position?.end.offset;
		if (start === undefined || end === undefined) return;
		if (node.type === "link") {
			if (isCitationHref(node.url))
				found.push({
					kind: "link",
					start,
					end,
					link: { href: node.url, title: Option.toUndefined(node.title) },
				});
			return;
		}
		if (node.type === "linkReference") return;
		if (node.type === "text") {
			found.push(...textCitations(text.slice(start, end), start));
			return;
		}
		if ("children" in node) for (const child of node.children) visit(child);
	};
	visit(assistantMarkdown.parse(text));
	return found;
};

const rewriteCitations = (text: string, rewrite: (citation: FoundCitation) => string): string => {
	let result = "";
	let start = 0;
	for (const citation of findCitations(text)) {
		result += text.slice(start, citation.start) + rewrite(citation);
		start = citation.end;
	}
	return result + text.slice(start);
};

/** Rewrites evidence ids written as text into citation links, so they render as numbered sources. */
export const withCitationLinks = (text: string): string =>
	rewriteCitations(text, (citation) =>
		citation.kind === "link"
			? text.slice(citation.start, citation.end)
			: citation.hrefs.map((href) => `[0](${href})`).join(""),
	);

const citationLinks = (text: string): CitationLink[] =>
	findCitations(text).flatMap((citation) =>
		citation.kind === "link"
			? [citation.link]
			: citation.hrefs.map((href) => ({ href, title: undefined })),
	);

/** Every citation's key, in the order its number is assigned. */
export const citationKeys = (text: string): string[] => [
	...new Set(citationLinks(text).map(({ href }) => citationKey(href))),
];

/** Answer text for the clipboard, where a `cite:` link means nothing: each becomes `(its title)`. */
export const withPlainCitations = (text: string): string =>
	rewriteCitations(text, (citation) =>
		citation.kind === "link" && citation.link.title !== undefined && citation.link.title !== ""
			? ` (${citation.link.title})`
			: "",
	);

/** Every record an answer cites, once each, in the order it first appears. */
export const citationEvidenceIds = (text: string): Assistant.Tools.EvidenceId[] => [
	...new Set(
		citationLinks(text).flatMap(({ href, title }) => {
			const citation = parseCitation(href, title);
			const evidenceId = citation === undefined ? undefined : citationEvidenceId(citation);
			return evidenceId === undefined ? [] : [evidenceId];
		}),
	),
];
